import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Lightning, User, List, ArrowUpRight, X } from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";
import { MARKETING_URL } from "@/lib/api";
import { FoundingBadge } from "@/components/dashboard/FoundingBadge";

const NAV_ITEMS = [
    { label: "Dashboard", to: "/dashboard", id: "dashboard" },
    { label: "Battery", to: "/battery", id: "battery" },
    { label: "Community", to: "/community", id: "community" },
    { label: "Report", to: "/report", id: "report" },
    { label: "Partners", to: "/partners", id: "partners" },
];

const linkClass = ({ isActive }) =>
    `text-sm transition-colors duration-200 ${
        isActive ? "text-white" : "text-zinc-500 hover:text-zinc-200"
    }`;

export const TopNav = ({ user }) => {
    const [open, setOpen] = useState(false);

    return (
        <nav
            data-testid={DASHBOARD.nav}
            className="sticky top-0 z-40 border-b border-[#1f1f22] bg-black/70 backdrop-blur-xl"
        >
            {/* Cross-site breadcrumb: this app reads as a subpage of eviq.pro */}
            <a
                href={MARKETING_URL}
                target="_top"
                rel="noopener"
                data-testid="parent-site-breadcrumb"
                className="block border-b border-[#1f1f22] bg-black/60"
            >
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-1.5 sm:px-6 lg:px-10">
                    <div className="flex items-center gap-2 font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                        <span className="hidden sm:inline">← eviq.pro</span>
                        <span className="sm:hidden">← eviq.pro</span>
                        <span className="text-zinc-700">/</span>
                        <span className="text-[#a3e635]">earth initiative</span>
                    </div>
                    <span className="hidden font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-600 md:inline">
                        part of eviq.pro
                    </span>
                </div>
            </a>

            <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 lg:px-10">
                <NavLink to="/dashboard" className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-[#27272a] bg-[#0a0a0a]">
                        <Lightning size={18} weight="fill" color="#a3e635" />
                    </div>
                    <div className="flex min-w-0 flex-col leading-tight">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                            EVIQ
                        </span>
                        <span className="truncate font-editorial text-base text-white sm:text-lg">
                            Earth Initiative
                        </span>
                    </div>
                </NavLink>

                <div className="hidden items-center gap-8 md:flex">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.id}
                            to={item.to}
                            data-testid={DASHBOARD.navLink(item.id)}
                            className={linkClass}
                            end={item.to === "/dashboard"}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <a
                        href={MARKETING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={DASHBOARD.marketingLink}
                        title="EVIQ · eviq.pro"
                        className="inline-flex items-center gap-1 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-3 py-1 text-xs text-[#a3e635] transition-colors duration-200 hover:bg-[#141a09]"
                    >
                        Marketing Site
                        <ArrowUpRight size={12} weight="bold" />
                    </a>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                    {user && (
                        <div className="hidden sm:block">
                            <FoundingBadge
                                tier={user.founder_tier}
                                memberNumber={user.member_number}
                                size="sm"
                                testId="nav-founding-badge"
                            />
                        </div>
                    )}
                    <button
                        type="button"
                        aria-label="Menu"
                        onClick={() => setOpen((o) => !o)}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-[#27272a] bg-[#0a0a0a] md:hidden"
                    >
                        {open ? <X size={16} color="#a1a1aa" /> : <List size={16} color="#a1a1aa" />}
                    </button>
                    <div className="hidden h-9 w-9 items-center justify-center rounded-full border border-[#27272a] bg-[#0a0a0a] sm:flex">
                        <User size={16} color="#a1a1aa" />
                    </div>
                </div>
            </div>

            {open && (
                <div className="border-t border-[#1f1f22] bg-black/95 md:hidden">
                    <div className="flex flex-col gap-1 px-4 py-3">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.id}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                data-testid={DASHBOARD.navLink(item.id)}
                                className={({ isActive }) =>
                                    `rounded-md px-3 py-2 text-sm ${
                                        isActive
                                            ? "bg-[#0f0f10] text-white"
                                            : "text-zinc-400 hover:bg-[#0a0a0a] hover:text-zinc-100"
                                    }`
                                }
                                end={item.to === "/dashboard"}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <a
                            href={MARKETING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid={DASHBOARD.marketingLink}
                            className="mt-1 inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm text-[#a3e635]"
                        >
                            Marketing Site
                            <ArrowUpRight size={12} weight="bold" />
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default TopNav;
